import { useCallback, useRef } from "react";
import { PencilIcon } from "lucide-react";

import { Icon } from "@/components/atoms/Icon";
import { HotkeyDecorator } from "@/components/atoms/HotkeyDecorator";
import { useContextHotkeys } from "@/hooks/useContextTree";
import { Button } from "@/components/ui/button";
import { cn } from "@/components/ui/utils";
import { useListContext } from "./List.hooks";

export function ListMultiSelectFooter<T>() {
  const {
    checkedRows,
    setCheckedRows,
    checkedData,
    data,
    onSubmitCheckedRows,
    onNewRow,
    getRowKey,
    isNullable,
    classes,
  } = useListContext<T>();

  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const hasCheckedRows = Object.values(checkedRows).some(
    (value) => value === true,
  );

  const handleClear = useCallback(() => {
    if (isNullable && hasCheckedRows) {
      setCheckedRows({});
    }
  }, [isNullable, hasCheckedRows, setCheckedRows]);

  const handleSubmit = useCallback(() => {
    if (onSubmitCheckedRows) {
      const updatedCheckedRows = data.filter(
        (item) => checkedRows[getRowKey(item)] === true,
      );
      const updatedUncheckedRows = data.filter(
        (item) => checkedRows[getRowKey(item)] !== true,
      );
      onSubmitCheckedRows(
        updatedCheckedRows,
        updatedUncheckedRows,
        checkedData.find((item) => checkedRows[getRowKey(item)])!,
      );
    }
  }, [onSubmitCheckedRows, checkedData, data, checkedRows, getRowKey]);

  useContextHotkeys({
    id: "list-submit",
    combos: { mac: "meta+enter", windows: "ctrl+enter", linux: "ctrl+enter" },
    callback: (event: KeyboardEvent) => {
      event.preventDefault();
      submitButtonRef.current?.click();
    },
  });

  return (
    <div
      key="submit-multi-select"
      className={cn(
        "min-w-0 box-border w-full p-1 flex items-center justify-end bg-popover gap-1",
        classes.listItem,
        "[border-top-width:1px] [border-top-style:solid] border-border",
      )}
    >
      {onNewRow && (
        <Button
          variant="ghost"
          size="sm"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onNewRow("")}
          className="[box-shadow:none] mr-auto"
        >
          <Icon ReactIcon={PencilIcon} />
          Edit options
        </Button>
      )}
      <Button
        variant="outline"
        size="sm"
        disabled={!hasCheckedRows || !isNullable}
        onClick={handleClear}
      >
        Clear all
      </Button>
      <Button
        size="sm"
        ref={submitButtonRef}
        data-context-overlay-submit=""
        disabled={isNullable === false && checkedData.length === 0}
        onClick={handleSubmit}
        className="pl-2 pr-1"
      >
        Submit
        <HotkeyDecorator
          hotkey={{
            mac: "meta+enter",
            windows: "ctrl+enter",
            linux: "ctrl+enter",
          }}
          color="white"
        />
      </Button>
    </div>
  );
}
